import { formatAppDate } from '../utility/dateLocale';
import { PageRecordFilters } from '../components/PageRecordFilters';
import { InvoiceMonthFilter } from '../components/InvoiceMonthFilter';
import { overlapsPageMonth } from '../utility/pageMonthFilter';
import { useMemo, useState, useEffect } from 'react';
import {
  Modal,
  Button,
  TextInput,
  Select,
  Group,
  Title,
  Text,
  NumberInput,
} from '@mantine/core';
import { DateInput } from "@mantine/dates";
import { type MRT_ColumnDef, type MRT_Row } from 'mantine-react-table';
import { ExportableMantineReactTable, useExportableTable } from '../utility/useExportableTable';
import { enableGlobalSearchOnColumns } from '../utility/mrtTableSearch';
import { ImportConfirmModal } from '../components/ImportConfirmModal';
import { TableExcelImportModal } from '../components/TableExcelImportModal';
import { classifyImportRows } from '../utility/excelDeletedColumn';
import { executeImportUpsert } from '../utility/executeImportUpsert';
import { mergeDefinedFields, presentRowKeys } from '../utility/excelImportHelpers';
import { readExcelFile, splitImportRowsById } from '../utility/tableExcelExport';
import { MRT_DensityState } from 'mantine-react-table';
import { readApiErrorMessage } from '../utility/apiErrors';
import Environment from '../utility/environment';
import { useMrtTableContainerProps, usePageTableShellStyle } from '../utility/mobilePageLayout';
import { useAuth } from '../contexts/AuthProvider';
import { useTranslation } from 'react-i18next';
import { buildMrtLocalization } from '../i18n/mrtLocale';


type Campaign = {
  id: number;
  uuid: string;
  name: string;
  start_date: string;
  end_date: string;
  currency: string;
  budget: number;
  deletedAt?: string | null;
};

type CampaignForm = Omit<Campaign, 'id' | 'uuid'>;

const initialForm: CampaignForm = {
    name: '',
    start_date: new Date(Date.now()).toISOString().split('T')[0],
    end_date: new Date(Date.now()).toISOString().split('T')[0],
    currency: 'USD',
    budget: 0,
};

export default function CampaignPage() {
  const { accessToken, actualEnterprise, user } = useAuth();
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [campaignFilter,setCampaignFilter]=useState<string[]>([]);
  const [currencyFilter,setCurrencyFilter]=useState<string[]>([]);
  const [minBudget,setMinBudget]=useState<number | ''>('');
  const [maxBudget,setMaxBudget]=useState<number | ''>('');
  const { t, i18n } = useTranslation();
  const tableShellStyle = usePageTableShellStyle();
  const mrtTableContainerProps = useMrtTableContainerProps();

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);

  const [modalOpened, setModalOpened] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [isEditingCampaign, setIsEditingCampaign] = useState(false);
  const [campaignForm, setCampaignForm] = useState<CampaignForm>(initialForm);
  const [errorMessage, setErrorMessage] = useState('');

  // Estado para armazenar o arquivo importado via dropzone
  const [autoImportFile, setAutoImportFile] = useState<File | null>(null);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [importConfirm, setImportConfirm] = useState<{
    newCount: number;
    updateCount: number;
    deleteCount: number;
    run: () => Promise<void>;
  } | null>(null);
  const [notification, setNotification] = useState<{ message: string; color: 'green' | 'red' } | null>(null);
  const [importModalOpened, setImportModalOpened] = useState(false);

  const nameLength = 50;

  const dateRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

  useEffect(() => {
    getCampaigns();
  }, [accessToken, actualEnterprise]);

  useEffect(() => {
    document.title = t('campaigns.pageTitle');
  }, [t, i18n.language]);

  const isFormValid = () => {
    return (
        campaignForm.name.trim() !== '' &&
        campaignForm.currency.trim() !== '' &&
        dateRegex.test(campaignForm.start_date) &&
        dateRegex.test(campaignForm.end_date) 
    );
  };

  const openModal = (campaign?: Campaign) => {
    if (campaign) {
      setEditingCampaign(campaign);
      setCampaignForm({
        name: campaign.name,
        budget: campaign.budget,
        end_date: campaign.end_date,
        start_date: campaign.start_date,
        currency: campaign.currency
      });
    } else {
      setEditingCampaign(null);
      setCampaignForm(initialForm);
    }
    setIsEditingCampaign(false);
    setModalOpened(true);
    setAutoImportFile(null);
    setErrorMessage('');
    setNotification(null);
    setImportErrors([]);
  };


  const getCampaigns = () => {
    fetch(`${Environment.VITE_API_URL}/api/v1/all_campaigns/${actualEnterprise?.id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        const formattedCampaigns: Campaign[] = data.map((campaign: any, index: number) => ({
          id: index + 1,
          uuid: campaign.id,
          name: campaign.name,
          currency: campaign.currency || '',
          budget: parseFloat(campaign.budget),
          start_date: new Date(campaign.start_date).toISOString().split('T')[0],
          end_date: new Date(campaign.end_date).toISOString().split('T')[0],
          deletedAt: campaign.deleted_at ?? null,
        }));

        setCampaigns(formattedCampaigns);
      })
      .catch((err) => console.error('Erro ao buscar clientes:', err));
  };

  const deleteCampaign = () => {
    fetch(`${Environment.VITE_API_URL}/api/v1/campaigns/${editingCampaign?.uuid}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })
      .then(async (response) => {
        if (!response.ok) {
            setNotification({ message: t('campaigns.deleteError'), color: 'red' });
          setErrorMessage(t('campaigns.deleteError'));
        }
        console.log(response);
        if (response.status == 200) {
          setModalOpened(false);
          getCampaigns();
        }
      })
      .catch(() => {
        // console.error(error);
        setModalOpened(true);
      });
  };

  const updateCampaign = () => {
    console.log(JSON.stringify(campaignForm));
    fetch(`${Environment.VITE_API_URL}/api/v1/campaigns/${editingCampaign?.uuid}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
      body: JSON.stringify(formatCampaign(campaignForm)),
    })
      .then((res) => res.json())
      .then((data) => {
        console.log(data);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setModalOpened(false);
        getCampaigns();
      });
  };

  const createContact = async () => {
    try {
      const response = await fetch(`${Environment.VITE_API_URL}/api/v1/campaigns/${actualEnterprise?.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
        body: JSON.stringify(formatCampaign(campaignForm)),
      });

      if (!response.ok) {
        const message = await readApiErrorMessage(response, t('common.saveFailed'));
        setNotification({ message, color: 'red' });
        return;
      }

      setModalOpened(false);
      getCampaigns();
    } catch (error) {
      console.error(error);
      setNotification({ message: t('common.saveFailed'), color: 'red' });
    }
  };

  const formatCampaign = (contact: CampaignForm) => {
    return {
      name: contact.name,
      start_date: contact.start_date,
      end_date: contact.end_date,
      currency: contact.currency,
      budget: Number(contact.budget),
    };
  };


  const parseRowDate = (value: unknown): string => {
    if (value == null || value === '') return '';
    if (typeof value === 'string' && dateRegex.test(value)) return value;
    const parsed = new Date(value as string | number);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString().split('T')[0];
    }
    return '';
  };

  const rowToCampaignForm = (row: Record<string, unknown>): CampaignForm => {
    const budgetValue = row[t('campaigns.columns.budget')] ?? row['Budget'] ?? row['Orçamento'] ?? row['Orcamento'];
    return {
      name: String(row[t('campaigns.columns.name')] ?? row['Name'] ?? row['Nome'] ?? ''),
      start_date: parseRowDate(
        row[t('campaigns.columns.startDate')] ?? row['Start Date'] ?? row['Data de Início'] ?? row['Data de Inicio'],
      ),
      end_date: parseRowDate(
        row[t('campaigns.columns.finishDate')]
          ?? row['Finish Date']
          ?? row['Data de Termino']
          ?? row['Data de Término'],
      ),
      currency: String(row[t('campaigns.columns.currency')] ?? row['Currency'] ?? row['Moeda'] ?? ''),
      budget:
        budgetValue !== undefined && budgetValue !== null && budgetValue !== ''
          ? Number(budgetValue)
          : 0,
    };
  };

  const validateCampaignForm = (campaign: CampaignForm, line: number): string[] => {
    const rowErrors: string[] = [];
    if (!campaign.name) rowErrors.push(t('campaigns.validation.nameRequired'));
    if (!campaign.currency) rowErrors.push(t('campaigns.validation.currencyRequired'));
    if (!campaign.budget) rowErrors.push(t('campaigns.validation.budgetRequired'));
    if (!campaign.start_date) rowErrors.push(t('campaigns.validation.startDateRequired'));
    if (campaign.start_date && !dateRegex.test(campaign.start_date)) {
      rowErrors.push(t('campaigns.validation.startDateFormat'));
    }
    if (!campaign.end_date) rowErrors.push(t('campaigns.validation.endDateRequired'));
    if (campaign.end_date && !dateRegex.test(campaign.end_date)) {
      rowErrors.push(t('campaigns.validation.endDateFormat'));
    }
    if (rowErrors.length > 0) {
      return [t('common.import.rowError', { line, errors: rowErrors.join(' ') })];
    }
    return [];
  };

  const executeCampaignImport = async (
    createForms: CampaignForm[],
    updateEntries: Array<{ id: string; form: CampaignForm }>,
    deleteIds: string[],
  ) => {
    const ok = await executeImportUpsert({
      createPayloads: createForms.map(formatCampaign),
      updateEntries: updateEntries.map((entry) => ({
        id: entry.id,
        payload: formatCampaign(entry.form),
      })),
      deleteIds,
      bulkCreateUrl: `${Environment.VITE_API_URL}/api/v1/campaigns_bulk/${actualEnterprise?.id}`,
      updateUrl: (id) => `${Environment.VITE_API_URL}/api/v1/campaigns/${id}`,
      deleteUrl: (id) => `${Environment.VITE_API_URL}/api/v1/campaigns/${id}`,
      buildRequest: ({ method, body }) => ({
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
        body: body !== undefined ? JSON.stringify(body) : undefined,
      }),
    });

    if (!ok) {
      setNotification({ message: t('common.import.failed'), color: 'red' });
      return;
    }

    setNotification({ message: t('common.import.success'), color: 'green' });
    setModalOpened(false);
    setImportModalOpened(false);
    getCampaigns();
  };

  // Função para enviar o arquivo importado
  const handleAutoImport = async () => {
    if (!autoImportFile) {
      return;
    }
    try {
      const rawData = await readExcelFile(autoImportFile);
      const existingIds = new Set(campaigns.map((campaign) => campaign.uuid));
      const classified = classifyImportRows(rawData, existingIds, t);
      const split = splitImportRowsById(classified.rowsForUpsert, existingIds);
      const errors: string[] = [...classified.errors];

      const createForms: CampaignForm[] = [];
      for (const row of split.creates) {
        const line = rawData.indexOf(row) + 2;
        const campaign = rowToCampaignForm(row);
        errors.push(...validateCampaignForm(campaign, line));
        createForms.push(campaign);
      }

      const updateEntries: Array<{ id: string; form: CampaignForm }> = [];
      for (const entry of split.updates) {
        const line = rawData.indexOf(entry.row) + 2;
        const existing = campaigns.find((campaign) => campaign.uuid === entry.id);
        if (!existing) {
          continue;
        }
        const patch = rowToCampaignForm(entry.row);
        const merged = mergeDefinedFields(
          {
            name: existing.name,
            start_date: existing.start_date,
            end_date: existing.end_date,
            currency: existing.currency,
            budget: existing.budget,
          },
          patch,
          presentRowKeys(entry.row),
        );
        errors.push(...validateCampaignForm(merged, line));
        updateEntries.push({ id: entry.id, form: merged });
      }

      if (errors.length > 0) {
        setNotification({ message: t('common.import.someFailed'), color: 'red' });
        setImportErrors(errors);
        return;
      }

      setImportConfirm({
        newCount: createForms.length,
        updateCount: updateEntries.length,
        deleteCount: classified.deleteIds.length,
        run: () => executeCampaignImport(createForms, updateEntries, classified.deleteIds),
      });
    } catch (error) {
      console.error('Erro ao processar arquivo:', error);
      setNotification({ message: t('common.import.processFailed'), color: 'red' });
    }
  };

  const columns = useMemo<MRT_ColumnDef<Campaign>[]>(
    () => enableGlobalSearchOnColumns(
      [
      {
        accessorKey: 'name',
        header: t('campaigns.columns.name'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'start_date',
        Cell: ({ cell }) => formatAppDate(cell.getValue<string>()),
        header: t('campaigns.columns.startDate'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'end_date',
        Cell: ({ cell }) => formatAppDate(cell.getValue<string>()),
        header: t('campaigns.columns.finishDate'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'currency',
        header: t('campaigns.columns.currency'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
      },
      {
        accessorKey: 'budget',
        header: t('campaigns.columns.budget'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
      },
    ],
      ['name'],
    ),
    [t, i18n.language]
  );

  

  const handleRowDoubleClick = (rowData: Campaign) => {
    setModalOpened(true);
    setEditingCampaign(rowData);
    setCampaignForm(rowData);
    setIsEditingCampaign(true);
    setErrorMessage('');
    console.log('Dados da linha clicada:', rowData);
    console.log("Tipo do budget", typeof rowData.budget);
  };

  const openImportModal = () => {
    setImportModalOpened(true);
    setAutoImportFile(null);
    setImportErrors([]);
    setNotification(null);
  };

  const campaignFilterOptions = useMemo(()=>campaigns.map(campaign=>({value:campaign.uuid,label:campaign.name})).sort((a,b)=>a.label.localeCompare(b.label)),[campaigns]);
  const currencyFilterOptions = useMemo(()=>Array.from(new Set(campaigns.map(campaign=>campaign.currency||'USD'))).sort().map(value=>({value,label:value})),[campaigns]);
  const budgetBounds=useMemo(()=>{
    const amounts=campaigns.filter(campaign=>currencyFilter.length===1&&(campaign.currency||'USD')===currencyFilter[0]).map(campaign=>Number(campaign.budget)).filter(Number.isFinite);
    const min=Math.min(0,...amounts.map(Math.floor));
    return {min,max:Math.max(min+1,...amounts.map(Math.ceil))};
  },[campaigns,currencyFilter]);
  const monthCampaigns = useMemo(()=>campaigns.filter(campaign=>
    overlapsPageMonth(campaign.start_date,campaign.end_date,selectedMonth)
    &&(!campaignFilter.length||campaignFilter.includes(campaign.uuid))
    &&(!currencyFilter.length||currencyFilter.includes(campaign.currency||'USD'))
    &&(user?.role!=='admin'||currencyFilter.length!==1||minBudget===''||campaign.budget>=minBudget)
    &&(user?.role!=='admin'||currencyFilter.length!==1||maxBudget===''||campaign.budget<=maxBudget)
  ),[campaigns,selectedMonth,campaignFilter,currencyFilter,minBudget,maxBudget,user?.role]);
  const { table, exportModal } = useExportableTable(
    {
      columns,
      data: monthCampaigns,
      localization: buildMrtLocalization(t),
      defaultColumn: { size: 40, minSize: 30, maxSize: 600 },
      enableColumnFilters: true,
      enableSorting: true,
      enableFullScreenToggle: true,
      enableGrouping: false,
      enableColumnActions: false,
      enablePagination: true,
      enableBottomToolbar: true,
      enableRowVirtualization: false,
      initialState: {
        density: 'sm' as MRT_DensityState,
        showGlobalFilter: true,
        pagination: {
          pageSize: 20,
          pageIndex: 0,
        },
      },
      mantineTableContainerProps: mrtTableContainerProps,
      mantineTableProps: {
        sx: {
          display: 'table',
          tableLayout: 'auto',
          borderCollapse: 'separate',
          '& .mantine-TableHeadCell-Content': {
            justifyContent: 'center',
            alignItems: 'center',
            padding: '1px 1px',
          },
          '& .mantine-TextInput-input': {
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            borderRadius: '4px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: '#000',
          },
          '& th, & td': {
            borderRadius: '4px',
          },
          '& thead': {
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: 'rgba(48, 113, 91, 0.9)',
            color: '#fff',
          },
        },
      },
      mantineTableBodyRowProps: ({ row }: { row: MRT_Row<Campaign> }) => ({
        onDoubleClick: () => handleRowDoubleClick(row.original),
        style: { cursor: 'pointer' },
      }),
    },
    {
      idField: 'uuid',
      filenamePrefix: 'campaigns',
      sheetName: 'Campaigns',
      getDeletedValue: (row) => Boolean(row.deletedAt),
      getCellValue: (row, columnId) => row[columnId as keyof Campaign],
      onOpenImport: openImportModal,
    },
  );

  const importModal = (
    <TableExcelImportModal
      opened={importModalOpened}
      onClose={() => setImportModalOpened(false)}
      onImport={handleAutoImport}
      file={autoImportFile}
      onFileChange={(file) => setAutoImportFile(file)}
      errors={importErrors}
      notification={notification}
    />
  );

  return (
    <>
      {/* Cabeçalho com botão */}
      <Group position="apart" align="center" mb="md">
        <Group spacing="lg">
          <Title order={1}>{t('campaigns.title')}</Title>
          <InvoiceMonthFilter value={selectedMonth} onChange={setSelectedMonth} label={t('common.monthFilter.campaignPeriod')}/>
        </Group>
        <Group>
          <Button variant="subtle" disabled={selectedMonth==='all'&&!campaignFilter.length&&!currencyFilter.length&&minBudget===''&&maxBudget===''&&!table.getState().globalFilter&&!table.getState().columnFilters.length}
            onClick={()=>{setSelectedMonth('all');setCampaignFilter([]);setCurrencyFilter([]);setMinBudget('');setMaxBudget('');table.setGlobalFilter('');table.setColumnFilters([]);}}>{t('invoices.filters.clear')}</Button>
          <Button onClick={() => openModal()}>{t('campaigns.addCampaigns')}</Button>
        </Group>
      </Group>

      <PageRecordFilters fields={[
        {key:'campaign',label:t('campaigns.columns.name'),placeholder:t('common.recordFilters.allCampaigns'),options:campaignFilterOptions,value:campaignFilter,onChange:setCampaignFilter},
        {key:'currency',label:t('campaigns.columns.currency'),placeholder:t('common.recordFilters.allCurrencies'),options:currencyFilterOptions,value:currencyFilter,onChange:value=>{setCurrencyFilter(value);setMinBudget('');setMaxBudget('');},single:true},
      ]} range={user?.role==='admin'?{min:budgetBounds.min,max:budgetBounds.max,value:[minBudget===''?budgetBounds.min:minBudget,maxBudget===''?budgetBounds.max:maxBudget],onChange:([min,max])=>{setMinBudget(min===budgetBounds.min?'':min);setMaxBudget(max===budgetBounds.max?'':max);},label:t('campaigns.columns.budget'),currency:currencyFilter[0]||'',disabled:currencyFilter.length!==1}:undefined}/>
      <div style={tableShellStyle}>
        <ExportableMantineReactTable table={table} exportModal={exportModal} importModal={importModal} />
      </div>

      <ImportConfirmModal
        opened={importConfirm != null}
        newCount={importConfirm?.newCount ?? 0}
        updateCount={importConfirm?.updateCount ?? 0}
        deleteCount={importConfirm?.deleteCount ?? 0}
        entityKey="campaigns"
        onCancel={() => setImportConfirm(null)}
        onConfirm={() => {
          const run = importConfirm?.run;
          setImportConfirm(null);
          void run?.();
        }}
      />

      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        centered
        title={
          editingCampaign
            ? t('campaigns.modal.updateCampaign')
            : t('campaigns.modal.addNewCampaign')
        }
        size="xl"
      >
        {/* -- Campos Obrigatórios -- */}
        <Group grow mb="sm">
              <TextInput
                label={t('campaigns.form.name')}
                placeholder={t('campaigns.form.namePlaceholder')}
                value={campaignForm.name}
                onChange={(e) =>
                  setCampaignForm({ ...campaignForm, name: e.target.value })
                }
                mb="sm"
                required
                maxLength={nameLength}
              />
            </Group>
            <Text className={`text-input-counter ${campaignForm.name.length > nameLength ? "error" : ""}`}>
                {t('common.characters', { count: campaignForm.name.length, max: nameLength })}
            </Text>

            <Group grow mb="sm">
              <DateInput
                label={t('campaigns.form.startDate')}
                value={
                  campaignForm.start_date
                    ? new Date(`${campaignForm.start_date}T00:00:00`)
                    : null
                }
                onChange={(e) =>
                  setCampaignForm({
                    ...campaignForm,
                    start_date: new Date(e as Date).toISOString().split('T')[0],
                  })
                }
                mb="sm"
                required
                popoverProps={{ withinPortal: true }}
              />
              <DateInput
                label={t('campaigns.form.finishDate')}
                value={
                    campaignForm.end_date
                    ? new Date(`${campaignForm.end_date}T00:00:00`)
                    : null
                }
                onChange={(e) =>
                  setCampaignForm({
                    ...campaignForm,
                    end_date: new Date(e as Date).toISOString().split('T')[0],
                  })
                }
                mb="sm"
                required
                popoverProps={{ withinPortal: true }}
              />
            </Group>

            <Group grow mb="sm">
            <Select
                label={t('campaigns.form.currency')}
                placeholder={t('common.select')}
                value={campaignForm.currency}
                onChange={(value) =>
                  setCampaignForm({
                    ...campaignForm,
                    currency: value as
                      | 'BRL'
                      | 'USD'
                  })
                }
                data={[
                  { value: 'BRL', label: 'BRL' },
                  { value: 'USD', label: 'USD' },
                ]}
                mb="sm"
                required
                disabled={true}
              />
            <NumberInput
                label={t('campaigns.form.budget')}
                value={campaignForm.budget}
                onChange={(value) => setCampaignForm({ ...campaignForm, budget: value || 0 })}
                mb="sm"
                required
                precision={2}
                />
            </Group>
            
              

            <Group position="right" mt="md">
              {isEditingCampaign && (
                <Button
                  onClick={deleteCampaign}
                  sx={{
                    backgroundColor: 'rgba(163, 0, 0, 0.9)',
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(121, 0, 0, 0.9)' },
                  }}
                >
                  {t('common.delete')}
                </Button>
              )}
              <Button
                onClick={isEditingCampaign ? updateCampaign : createContact}
                disabled={!isFormValid()}
              >
                {isEditingCampaign ? t('common.update') : t('common.save')}
              </Button>
            </Group>

        <Group position="center" mt="md" color="red">
          <Text className="error-message" color="red">
            {errorMessage !== '' ? errorMessage : null}
          </Text>
        </Group>
      </Modal>

    </>
  );
}
